import type { PublicStats } from '~/server/api/stats/public.get'

/**
 * Rounds a live counter down to a "marketing-friendly" figure with a plus sign,
 * so the number is always true (never higher than reality):
 *   7 → "7", 48 → "40+", 1 234 → "1,200+", 15 678 → "15,000+".
 * Returns an empty string for null/0 so callers can hide the tile.
 */
export function formatCount(value: number | null | undefined): string {
  if (!value || value <= 0) return ''
  if (value < 10) return String(value)

  const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(value)) - 1)
  const rounded = Math.floor(value / magnitude) * magnitude
  return `${rounded.toLocaleString('en-US')}+`
}

/**
 * Shared, SSR-friendly access to /api/stats/public.
 * `useFetch` with a fixed key dedupes the request across all components on a page.
 */
export function usePublicStats() {
  const { data, pending } = useFetch<PublicStats>('/api/stats/public', {
    key: 'public-stats',
    server: true,
    lazy: true,
  })

  const stats = computed<PublicStats | null>(() => (data.value as PublicStats | null | undefined) ?? null)

  return {
    stats,
    pending,
    formatCount,
  }
}
